const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'https://assessment-engine.pure-bookkeeping6950.workers.dev'

// ── Types ────────────────────────────────────────────────────────────────────

export interface PortalCohortSession {
  id: string
  title: string
  scheduled_at: string | null
  sort_order: number
  replay_state: string | null
  replay_smart_link_slug: string | null
  recording_url: string | null
  zoom_join_url: string | null
  status: string
}

export interface PortalCohortHub {
  cohort_id: string
  cohort_name: string
  cohort_starts: string | null
  cohort_ends: string | null
  announcement: string | null
  community_url: string | null
  sessions: PortalCohortSession[]
}

export interface PortalEnrollee {
  id: string
  program_slug: string
  program_name: string
  program_label_override: string | null
  state: string
  started_at: string
  day_of_arc: number
  /** 'program' | 'orientation' (a member's or BKOS buyer's first week). */
  program_kind?: string
  progress: { complete: number; total: number; overdue: number }
  cohort_hub: PortalCohortHub | null
}

/** One orientation program's first-week steps. */
export interface PortalOrientation {
  enrollee_id: string
  program_slug: string
  program_name: string
  steps: Array<{
    id: string
    name: string
    description: string | null
    state: string
    can_mark_done: boolean
    /** Where to do a signal step, e.g. the kickoff booking page. */
    action_url?: string | null
    action_label?: string | null
  }>
}

export interface PortalNextMilestone {
  id: string
  enrollee_id: string
  program_slug: string
  program_name: string
  state: string
  effective_state: string
  target_at: string | null
  proposed_at: string | null
  name: string
  user_facing_description: string | null
  type: string
  /** False for a first-week step that ticks itself off from its own signal. */
  can_mark_done?: boolean
  action_url?: string | null
  action_label?: string | null
}

export interface PortalMeResult {
  person: {
    id: string
    primary_email: string
    display_name: string | null
    first_name: string | null
    last_name: string | null
  }
  brand: {
    portalLogoUrl: string | null
    portalLogoWhiteUrl: string | null
  }
  enrollees: PortalEnrollee[]
  next_milestones: PortalNextMilestone[]
  orientation?: PortalOrientation[]
}

export interface PortalMilestone {
  id: string
  template_id: string
  state: string
  effective_state: string
  target_at: string | null
  proposed_at: string | null
  completed_at: string | null
  dismissed_at: string | null
  position: number
  phase_role: string | null
  phase_type: string | null
  name: string
  user_facing_description: string | null
  type: string
  target_day: number | null
  celebration_message: string | null
  can_mark_done?: boolean
  action_url?: string | null
  action_label?: string | null
}

export interface PortalEnrolleeDetail {
  enrollee: {
    id: string
    program_slug: string
    program_name: string
    program_label_override: string | null
    state: string
    started_at: string
    day_of_arc_offset_days: number
    brand_palette: string | null
  }
  milestones: PortalMilestone[]
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const SESSION_KEY = 'portal:session_token'

export function getToken(): string | null {
  return localStorage.getItem(SESSION_KEY)
}

export function setToken(token: string) {
  localStorage.setItem(SESSION_KEY, token)
}

export function clearToken() {
  localStorage.removeItem(SESSION_KEY)
}

async function req<T>(path: string, opts: RequestInit = {}, token?: string | null): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token ?? getToken()
  if (t) headers['Authorization'] = `Bearer ${t}`
  const res = await fetch(`${BASE}${path}`, { ...opts, headers: { ...headers, ...(opts.headers ?? {}) } })
  const text = await res.text()
  let data: unknown
  try { data = text ? JSON.parse(text) : null } catch { data = null }
  if (!res.ok) {
    const msg = (data as { error?: string } | null)?.error ?? text ?? `HTTP ${res.status}`
    throw new Error(msg)
  }
  return data as T
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export async function requestMagicLink(email: string): Promise<void> {
  await req('/api/portal/auth/request-link', {
    method: 'POST',
    body: JSON.stringify({ email }),
  })
}

export async function verifyMagicLink(token: string): Promise<{ ok: boolean; sessionToken?: string; reason?: string }> {
  return req('/api/portal/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  })
}

// ── Me ───────────────────────────────────────────────────────────────────────

export async function getMe(): Promise<PortalMeResult> {
  return req<PortalMeResult>('/api/portal/me')
}

// ── Enrollees ────────────────────────────────────────────────────────────────

export async function getEnrollee(enrolleeId: string): Promise<PortalEnrolleeDetail> {
  return req<PortalEnrolleeDetail>(`/api/portal/enrollees/${encodeURIComponent(enrolleeId)}`)
}

// ── Milestone actions ────────────────────────────────────────────────────────

export async function attestMilestone(id: string, note?: string): Promise<{ ok: boolean; newState?: string }> {
  return req(`/api/portal/milestones/${encodeURIComponent(id)}/attest`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  })
}

export async function dismissMilestone(id: string, note?: string): Promise<unknown> {
  return req(`/api/portal/milestones/${encodeURIComponent(id)}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  })
}

export async function confirmMilestone(id: string, note?: string): Promise<unknown> {
  return req(`/api/portal/milestones/${encodeURIComponent(id)}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  })
}

// ── Support ──────────────────────────────────────────────────────────────────

export async function sendSupportRequest(message: string, enrolleeId?: string, category?: string): Promise<unknown> {
  return req('/api/portal/support-request', {
    method: 'POST',
    body: JSON.stringify({ message, enrolleeId, category }),
  })
}

// ── Notifications ────────────────────────────────────────────────────────────

export interface PortalReminderRow {
  kind: 'call' | 'program' | 'webinar'
  ref: string
  label: string
  nextAt: string | null
  /** True = this member has turned reminders OFF for the call. */
  muted: boolean
}

export interface PortalDigest {
  mode: 'regional' | 'edition' | 'global' | 'off'
  edition: string | null
  placed: { key: string; name: string }
  editions: Array<{ key: string; name: string }>
}

export async function setWeeklyDigest(
  choice: { mode: PortalDigest['mode']; edition?: string },
): Promise<{ digest: PortalDigest | null; changed: boolean }> {
  return req('/api/portal/notifications/digest', { method: 'POST', body: JSON.stringify(choice) })
}

export async function getNotifications(): Promise<{ reminders: PortalReminderRow[]; digest: PortalDigest | null }> {
  return req('/api/portal/notifications')
}

export async function setReminderMuted(
  kind: PortalReminderRow['kind'],
  ref: string,
  muted: boolean,
): Promise<{ reminders: PortalReminderRow[]; changed: boolean }> {
  return req('/api/portal/notifications/mutes', {
    method: 'POST',
    body: JSON.stringify({ item_kind: kind, item_ref: ref, muted }),
  })
}
